import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Crosshair, Trophy, Star, Send, Mail, Phone, MapPin, ArrowUpRight, Gamepad2, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { skills, achievements, profile, contactInfo } from '../../data/mock';
import { Badge } from '../ui/badge';
import { parse, isValid } from 'date-fns';

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

const categories = ['all', 'language', 'devops', 'architecture', 'blockchain', 'cloud', 'database', 'systems', 'tools'];

const rarityColors = {
  LEGENDARY: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/25', glow: 'shadow-amber-500/10' },
  EPIC: { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/25', glow: 'shadow-purple-500/10' },
  RARE: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/25', glow: 'shadow-cyan-500/10' },
};

function getCertificationStatus(expires) {
  if (!expires || expires === 'No Expiry') {
    return {
      label: 'No expiry',
      badge: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    };
  }

  const parsed = parse(expires, 'MMM yyyy', new Date());

  if (!isValid(parsed)) {
    return {
      label: expires,
      badge: 'bg-zinc-500/10 border-zinc-500/20 text-zinc-400',
    };
  }

  const expiryDate = new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0, 23, 59, 59, 999);
  const isExpired = expiryDate < new Date();

  return {
    label: isExpired ? `Expired ${expires}` : `Valid through ${expires}`,
    badge: isExpired ? 'bg-rose-500/10 border-rose-500/20 text-rose-400' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  };
}

function SkillTree() {
  const [filter, setFilter] = useState('all');
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const filtered = filter === 'all' ? skills : skills.filter(s => s.category === filter);

  return (
    <section id="skills" className="relative py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <Crosshair size={20} className="text-emerald-400" />
            <span className="font-mono text-xs text-emerald-400/60 uppercase tracking-widest">// Skill Tree</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-zinc-100" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Tech Arsenal
          </h2>
          <p className="text-zinc-500 mt-2 max-w-lg" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Weapons honed through years of building distributed, high-performance systems.
          </p>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-3 py-1.5 rounded-lg font-mono text-xs transition-all capitalize ${
                filter === cat
                  ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
                  : 'bg-[#0f0f18] border border-[#1a1a2e] text-zinc-500 hover:text-zinc-300 hover:border-[#252540]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Skills grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((skill, i) => {
            const Icon = skill.icon;
            return (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, y: 15 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.04 }}
                className="group rounded-lg border border-[#1a1a2e] bg-[#0a0a14]/60 p-4 hover:border-emerald-500/20 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-md bg-[#111120] border border-[#1a1a2e] flex items-center justify-center group-hover:border-emerald-500/20 transition-colors">
                      <Icon size={15} className="text-zinc-500 group-hover:text-emerald-400 transition-colors" />
                    </div>
                    <span className="font-mono text-sm text-zinc-300">{skill.name}</span>
                  </div>
                  <span className="font-mono text-xs text-emerald-400/70">{skill.level}</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-[#1a1a2e] overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${skill.level}%` } : {}}
                    transition={{ duration: 0.8, delay: 0.3 + i * 0.04 }}
                    className="h-full rounded-full bg-gradient-to-r from-emerald-500/80 to-emerald-400/60"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Achievements() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="achievements" className="relative py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <Trophy size={20} className="text-amber-400" />
            <span className="font-mono text-xs text-amber-400/60 uppercase tracking-widest">// Achievements</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-zinc-100" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Badges Unlocked
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {achievements.map((ach, i) => {
            const Icon = ach.icon;
            const rarity = rarityColors[ach.rarity];
            const certStatus = getCertificationStatus(ach.expires);
            return (
              <motion.div
                key={ach.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.2 + i * 0.1 }}
                className={`relative rounded-xl border ${rarity.border} ${rarity.bg} p-5 shadow-lg ${rarity.glow} group hover:scale-[1.02] transition-transform`}
              >
                {/* Rarity tag */}
                <Badge variant="outline" className={`${rarity.bg} ${rarity.border} ${rarity.text} font-mono text-[9px] px-2 py-0.5 mb-4`}>
                  <Star size={10} className="mr-1" />
                  {ach.rarity}
                </Badge>

                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ border: `1px solid ${ach.color}30`, backgroundColor: ach.color + '10' }}>
                  <Icon size={22} style={{ color: ach.color }} />
                </div>

                <h3 className="font-semibold text-zinc-100 text-sm mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {ach.title}
                </h3>
                <p className="font-mono text-xs text-zinc-500 mb-3">{ach.issuer}</p>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] text-zinc-600">Earned: {ach.date}</span>
                  <Badge variant="outline" className={`font-mono text-[9px] px-2 py-0.5 ${certStatus.badge}`}>
                    {certStatus.label}
                  </Badge>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    try {
      // Send to Web3Forms for email delivery to teja@surisetty.dev
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          name: formData.name,
          email: formData.email,
          message: formData.message,
          subject: `New Quest Request from ${formData.name}`,
          from_name: 'Portfolio Contact Form',
        }),
      });

      const data = await res.json();

      if (data.success) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus('idle'), 4000);
      } else {
        throw new Error(data.message || 'Failed to send');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Try emailing directly.');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section id="contact" className="relative py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}>
          <div className="flex items-center gap-3 mb-3">
            <Gamepad2 size={20} className="text-emerald-400" />
            <span className="font-mono text-xs text-emerald-400/60 uppercase tracking-widest">// Co-op Mode</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-zinc-100 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>
            {contactInfo.heading}
          </h2>
          <p className="text-zinc-500 text-base max-w-lg mb-10" style={{ fontFamily: 'Figtree, sans-serif' }}>
            {contactInfo.subheading}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-[1fr_280px] gap-8">
          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="rounded-xl border border-[#1a1a2e] bg-[#0a0a14]/60 p-6 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider mb-1.5 block">Player Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f18] border border-[#1a1a2e] text-zinc-300 font-mono text-sm placeholder:text-zinc-700 focus:border-emerald-500/40 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all"
                  placeholder="Enter your name"
                  required
                />
              </div>
              <div>
                <label className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider mb-1.5 block">Comms Channel</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f18] border border-[#1a1a2e] text-zinc-300 font-mono text-sm placeholder:text-zinc-700 focus:border-emerald-500/40 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider mb-1.5 block">Quest Details</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={5}
                className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f18] border border-[#1a1a2e] text-zinc-300 font-mono text-sm placeholder:text-zinc-700 focus:border-emerald-500/40 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all resize-none"
                placeholder="Describe your quest or project..."
                required
              />
            </div>
            <button
              type="submit"
              disabled={status === 'sending'}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#050508] font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              {status === 'sending' && (
                <><Loader2 size={16} className="animate-spin" /> Sending...</>
              )}
              {status === 'success' && (
                <><CheckCircle2 size={16} /> Quest Sent!</>
              )}
              {status === 'error' && (
                <><AlertCircle size={16} /> Try Again</>
              )}
              {status === 'idle' && (
                <><Send size={16} /> Send Quest Request</>
              )}
            </button>
            {status === 'success' && (
              <p className="font-mono text-xs text-emerald-400 mt-2">Message delivered to teja@surisetty.dev</p>
            )}
            {status === 'error' && (
              <p className="font-mono text-xs text-rose-400 mt-2">{errorMsg}</p>
            )}
          </motion.form>

          {/* Contact sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 p-4 rounded-xl border border-[#1a1a2e] bg-[#0a0a14]/60 hover:border-emerald-500/20 transition-colors group">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Mail size={16} className="text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-mono text-[10px] text-zinc-600">EMAIL</span>
                <p className="text-zinc-300 text-sm font-mono truncate">{contactInfo.email}</p>
              </div>
              <ArrowUpRight size={14} className="text-zinc-700 group-hover:text-emerald-400 transition-colors" />
            </a>

            <div className="flex items-center gap-3 p-4 rounded-xl border border-[#1a1a2e] bg-[#0a0a14]/60">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                <Phone size={16} className="text-cyan-400" />
              </div>
              <div>
                <span className="font-mono text-[10px] text-zinc-600">PHONE</span>
                <p className="text-zinc-300 text-sm font-mono">{contactInfo.phone}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 rounded-xl border border-[#1a1a2e] bg-[#0a0a14]/60">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                <MapPin size={16} className="text-amber-400" />
              </div>
              <div>
                <span className="font-mono text-[10px] text-zinc-600">BASE</span>
                <p className="text-zinc-300 text-sm font-mono">{profile.location}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#1a1a2e] py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/20 flex items-center justify-center">
              <span className="font-mono text-xs text-emerald-400 font-bold">TS</span>
            </div>
            <span className="font-mono text-xs text-zinc-600">
              <span className="text-zinc-500">Teja Surisetty</span> · Senior Platform Engineer
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href={`mailto:${profile.email}`} className="font-mono text-xs text-zinc-500 hover:text-emerald-400 transition-colors">
              {profile.email}
            </a>
            <span className="text-zinc-800">|</span>
            <span className="font-mono text-xs text-zinc-600">
              Built with passion & Go-speed
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export { SkillTree, Achievements, Contact, Footer };
